import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import SideNav from "../SideNav";
import TopNav from "../TopNav";
import { useSidebarContext } from "../../context/sidebarContext";
import { useDashboardLayoutController } from "../../controllers/useDashboardLayoutController";
import { useTour } from "../../hooks/useTour";
import KYCModal from "../../components/KYCModal";
import FeedbackModal from "../Feedback/FeedbackModal";
import { useSubscriptionGuard } from "../../hooks/useSubscriptionGuard";

interface DashboardLayoutProps {
  children?: React.ReactNode;
  title?: string | React.ReactNode;
  containerClass?: string;
  description?: string;
  onDataRefresh?: () => void;
  dateRange?: [Date | null, Date | null];
  onDateRangeChange?: (range: [Date | null, Date | null]) => void;
  showDateRange?: boolean;
  pagePath?: React.ReactNode;
  className?: string;
}

// const DashboardLayout = ({
//   children,
//   title,
//   containerClass,
//   description,
//   onDataRefresh,
//   dateRange,
//   onDateRangeChange,
//   pagePath,
//   showDateRange = false,
//   className,
// }: DashboardLayoutProps) => {
//   const { titleVariants, hasMounted, kycDone, setKycDone } = useDashboardLayoutController();
//   const { isSideRes, setIsSideRes } = useSidebarContext();

const DashboardLayout = ({
  children,
  title,
  containerClass,
  description,
  onDataRefresh,
  dateRange,
  onDateRangeChange,
  pagePath,
  showDateRange = false,
  className,
}: DashboardLayoutProps) => {
  const { tourDone } = useTour();
  const { titleVariants, hasMounted, kycDone, kycChecking, setKycDone, feedbackOpen, closeFeedback, remindLaterFeedback, submitFeedback } = useDashboardLayoutController(tourDone);
  const { isSideRes, setIsSideRes } = useSidebarContext();
  const location = useLocation();
  useSubscriptionGuard();

  return (
    <div className="flex h-screen overflow-hidden">
      <TopNav
        onMenuClick={() => setIsSideRes(!isSideRes)}
        isSideRes={isSideRes}
      />
      <SideNav />
      <main
        className={`flex-1 mt-[73px] bg-[#F5F6FA] relative overflow-y-auto hide-scrollbar ${containerClass}`}
      >
        <div className="px-1 md:px-6 mt-8">
          <div className="flex items-center justify-between flex-wrap">
            {hasMounted && typeof title === "string" ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key={title}
                  className="flex items-center gap-3"
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={titleVariants}
                >
                  <div className="md:mb-0 lg:mb-0 w-16 h-10 lg:w-10 flex items-center justify-center rounded-lg bg-gradient-to-b from-[#f5f7fb] to-[#9fb4df]">
                    <span className="text-[#143887] font-semibold text-lg">
                      {title.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h6 className="text-[25px] font-semibold text-[#143887] leading-tight">
                      {title}
                    </h6>
                    {description && (
                      <p className="text-sm text-primary mt-1 max-w-[600px]">
                        {description}
                      </p>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            ) : (
              typeof title === "string" && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-b from-white to-[#d3ddf0] border border-[#c1cce8] shadow-sm">
                    <span className="text-[#143887] font-semibold text-lg">
                      {title.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h6 className="text-[20px] font-semibold text-[#143887] leading-tight">
                      {title}
                    </h6>
                    {description && (
                      <p className="text-sm text-primary mt-1 max-w-[600px]">
                        {description}
                      </p>
                    )}
                  </div>
                </div>
              )
            )}
          </div>

          {pagePath && (
            <div className="mt-6">
              {pagePath}
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>


      {/*  Blocks dashboard until KYC is done */}
      {/* Wait for the backend KYC check so a verified company never sees the form flash */}
      {!kycDone && !kycChecking && (
        <KYCModal onSuccess={() => setKycDone(true)} />
      )}

      <FeedbackModal
      isOpen={feedbackOpen}
      onClose={closeFeedback}
      onRemindLater={remindLaterFeedback}
      onSubmit={submitFeedback}
      />
    </div>
  );
};

export default DashboardLayout;